import type { AgentConnection, Job } from '@cef-ai/vault-sdk';
import { AgentAvatar, formatRelativeTime } from '@cere/cere-design-system';
import InsightsIcon from '@mui/icons-material/Insights';
import type { AgentCard } from '@/src/data/agent-cards';
import { agentAnchor } from '@/src/data/anchors';
import { buildActivityFeed, formatDuration } from '@/src/data/activity-feed';
import { jobStateLabel, jobStateTone } from '@/src/lib/runStatusChipProps';

interface Props {
  jobs: Job[];
  agents: AgentConnection[];
  agentCards: Record<string, AgentCard>;
  onSelectAnchor: (anchor: string) => void;
}

/** "What changed recently, jump to it" — one card per agent, most-recent first. */
export function ActivityFeed({ jobs, agents, agentCards, onSelectAnchor }: Props) {
  const rows = buildActivityFeed(jobs, agentCards, agents);
  if (rows.length === 0) return null;

  return (
    <section className="vault-block vault-feed-block" data-anchor="section-activity">
      <header className="vault-feed-block__head">
        <span className="vault-feed-block__head-icon" aria-hidden>
          <InsightsIcon sx={{ fontSize: 18, color: 'currentColor' }} />
        </span>
        <h2 className="vault-feed-block__title">Recent activity</h2>
      </header>
      <ul className="vault-feed-block__list">
        {rows.map((row) => {
          const duration = formatDuration(row.durationMs);
          return (
            <li key={row.agentId}>
              <button
                type="button"
                className="vault-feed-row"
                onClick={() => onSelectAnchor(agentAnchor(row.agentId))}
              >
                <AgentAvatar seed={row.agentId} name={row.name} size={38} iconUrl={row.iconUrl} />
                <span className="vault-feed-row__body">
                  <span className="vault-feed-row__name">{row.name}</span>
                  <span className="vault-feed-row__meta">
                    <span className="vault-feed-row__when">{formatRelativeTime(row.updatedAt)}</span>
                    {duration && (
                      <>
                        <span className="vault-feed-row__meta-sep" aria-hidden>·</span>
                        <span className="vault-feed-row__dur">ran {duration}</span>
                      </>
                    )}
                  </span>
                </span>
                <span className="vault-feed-row__chip" data-tone={jobStateTone(row.state)}>
                  {jobStateLabel(row.state)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
